
const CHAT_API_URL = import.meta.env.VITE_CHAT_API_URL || 'https://chat.nisoo.com/api/v1';
export const CHAT_API_BASE_URL = CHAT_API_URL.replace('/api/v1', '');
export const CHAT_OIDC_LOGIN_URL = CHAT_API_BASE_URL;

interface ChatLoginParams {
  token?: string;
}

// Login to chat API with Keycloak token
export const chatLogin = async ({ token }: ChatLoginParams) => {
  if (!token) {
    throw new Error('No authentication token provided');
  }

  try {
    console.log('Logging into chat service with token...');

    // First check if there's an existing session
    const sessionResponse = await fetch(`${CHAT_API_URL}/auths/session`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    });

    // If session exists and is valid, no need to login again
    if (sessionResponse.ok) {
      console.log('Already logged in to chat service');
      return { success: true };
    }

    // If no valid session, perform login
    const loginResponse = await fetch(`${CHAT_API_URL}/auths/signin`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({ token })
    });

    if (!loginResponse.ok) {
      const errorData = await loginResponse.json().catch(() => ({}));
      throw new Error(errorData.message || `Chat login failed with status: ${loginResponse.status}`);
    }

    console.log('Successfully logged in to chat service');
    return { success: true };
  } catch (error) {
    console.error('Chat login error:', error);
    throw error;
  }
};

// Logout from chat API
export const chatLogout = async () => {
  try {
    console.log('Signing out from chat service...');
    const response = await fetch(`${CHAT_API_URL}/auths/signout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.warn('Chat logout warning:', errorData.message || `Status: ${response.status}`);
    }

    console.log('Successfully signed out from chat service');
    return { success: true };
  } catch (error) {
    console.error('Chat logout error:', error);
    // Don't throw the error, just log it and continue with Keycloak logout
    return { success: false, error };
  }
};

// Verify session status with chat API
export const verifyChatSession = async (token?: string) => {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${CHAT_API_URL}/auths/session`, {
      method: 'GET',
      headers,
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`Chat session verification failed with status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Chat session verification error:', error);
    throw error;
  }
};

// Legacy methods kept for backward compatibility
export const chatSignup = async (username: string, email: string, token: string) => {
  console.warn('chatSignup is deprecated. Use chatLogin instead');
  return await chatLogin({ token });
};

export const chatSignin = async (email: string, token: string) => {
  console.warn('chatSignin is deprecated. Use chatLogin instead');
  return await chatLogin({ token });
};

export const chatSignout = chatLogout;
